import { z } from 'zod';
export const KNOWLEDGE_LIFECYCLE_VERSION = 'knowledge-lifecycle-v1';
export const KNOWLEDGE_FAILURE_METADATA_KEY = 'processingFailure';
const verdict = z.enum(['proven_true', 'proven_false', 'not_established']);
const safeCount = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable();
export const knowledgeFailureReceiptSchema = z.object({
    version: z.literal(KNOWLEDGE_LIFECYCLE_VERSION),
    origin: z.enum(['embedding', 'processing']),
    code: z.enum(['INVALID_SCOPE', 'INCOMPATIBLE_INDEX', 'MISSING_CREDENTIAL', 'INVALID_CREDENTIAL',
        'REQUEST_FAILED', 'INVALID_RESPONSE', 'PROCESSING_FAILED']),
    httpStatus: z.number().int().min(400).max(599).nullable(),
}).strict().refine(receipt => receipt.origin === 'processing'
    ? receipt.code === 'PROCESSING_FAILED' && receipt.httpStatus === null
    : receipt.code !== 'PROCESSING_FAILED' && (receipt.code === 'REQUEST_FAILED' || receipt.httpStatus === null), { message: 'Failure fields must come from the same recognized producer outcome.' });
export const knowledgeDocumentLifecycleSchema = z.object({
    version: z.literal(KNOWLEDGE_LIFECYCLE_VERSION),
    stored: verdict,
    source: z.object({
        edition: z.enum(['first', 'replacement', 'current', 'unknown']),
        revisionId: z.string().max(200).nullable(),
        fileName: z.string().max(500).nullable(),
        sizeBytes: safeCount,
    }).strict(),
    indexing: z.object({
        phase: z.enum(['not_started', 'queued', 'running', 'completed', 'failed', 'unconfirmed', 'removed']),
        verdict,
    }).strict(),
    search: z.object({ verdict, chunkCount: safeCount }).strict(),
    failure: knowledgeFailureReceiptSchema.nullable(),
    qualifications: z.array(z.enum(['source_unconfirmed', 'index_unconfirmed', 'native_outcome_unconfirmed',
        'legacy_source_unverified'])).max(4),
}).strict();
function record(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function id(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 200 ? value : null;
}
function bytes(value) {
    if (typeof value === 'bigint')
        return value >= 0n && value <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(value) : null;
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}
function name(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 500 ? value : null;
}
export function projectKnowledgeDocumentLifecycle(value) {
    const document = record(value);
    const pendingId = id(document.pendingRevisionId);
    const activeId = id(document.activeRevisionId);
    const pendingKnown = document.pendingRevisionId === null || pendingId !== null;
    const activeKnown = document.activeRevisionId === null || activeId !== null;
    const embedded = readKnowledgeDocumentLifecycle(document.lifecycle);
    const removed = Boolean(document.deletedAt);
    const embeddedPhaseMatches = removed ? embedded?.indexing.phase === 'removed'
        : embedded?.indexing.phase === 'removed' ? false
            : embedded?.indexing.phase === 'completed' ? !pendingId && ['READY', 'VECTORIZED'].includes(String(document.status))
                : embedded?.indexing.phase === 'failed' ? document.status === 'FAILED'
                    : embedded?.indexing.phase === 'running' || embedded?.indexing.phase === 'queued' ? document.status === 'PROCESSING'
                        : embedded?.indexing.phase === 'not_started' ? document.status === 'UPLOADED' : true;
    if (embedded && pendingKnown && activeKnown && embedded.source.revisionId === (pendingId ?? activeId) && embeddedPhaseMatches)
        return embedded;
    const pending = record(document.pendingRevision);
    const active = record(document.activeRevision);
    const selectedId = pendingId ?? activeId;
    const selected = pendingId ? pending : activeId ? active : document;
    const sourceEstablished = selectedId ? id(selected.id) === selectedId : pendingKnown && activeKnown;
    const qualifications = [];
    if (!sourceEstablished)
        qualifications.push('source_unconfirmed');
    const metadata = record(selected.metadata);
    const parsedFailure = knowledgeFailureReceiptSchema.safeParse(selectedId && sourceEstablished
        ? metadata[KNOWLEDGE_FAILURE_METADATA_KEY] : undefined);
    const failed = selectedId ? sourceEstablished && selected.status === 'FAILED'
        : pendingKnown && activeKnown && document.status === 'FAILED';
    const failure = failed
        ? parsedFailure.success ? parsedFailure.data : {
            version: KNOWLEDGE_LIFECYCLE_VERSION, origin: 'processing', code: 'PROCESSING_FAILED', httpStatus: null,
        } : null;
    const nativeUnknown = failure?.origin === 'embedding' && failure.code === 'REQUEST_FAILED' && failure.httpStatus === null;
    if (nativeUnknown)
        qualifications.push('native_outcome_unconfirmed');
    let phase = 'unconfirmed';
    if (removed)
        phase = 'removed';
    else if (nativeUnknown)
        phase = 'unconfirmed';
    else if (failed)
        phase = 'failed';
    else if (pendingId && sourceEstablished && pending.status === 'PENDING')
        phase = 'queued';
    else if (pendingId && sourceEstablished && pending.status === 'PROCESSING')
        phase = 'running';
    else if (!pendingId && activeId && sourceEstablished && active.status === 'PUBLISHED' &&
        ['VECTORIZED', 'READY'].includes(String(document.status)))
        phase = 'completed';
    else if (pendingKnown && activeKnown && !pendingId && !activeId && document.status === 'UPLOADED')
        phase = 'not_started';
    else if (pendingKnown && activeKnown && !pendingId && !activeId && document.status === 'PROCESSING')
        phase = 'running';
    const currentCount = bytes(record(document._count).chunks);
    const legacy = !removed && document.pendingRevisionId === null && document.activeRevisionId === null
        && ['VECTORIZED', 'READY'].includes(String(document.status));
    if (legacy && currentCount !== null && currentCount > 0) {
        phase = 'completed';
        qualifications.push('legacy_source_unverified');
    }
    if (phase === 'unconfirmed')
        qualifications.push('index_unconfirmed');
    const knownStatus = typeof document.status === 'string' && ['UPLOADED', 'PROCESSING', 'VECTORIZED', 'READY', 'FAILED'].includes(document.status);
    const excluded = removed || Boolean(pendingId) || knownStatus && !['VECTORIZED', 'READY'].includes(String(document.status));
    const searchable = excluded ? 'proven_false' : !knownStatus || !pendingKnown || currentCount === null ? 'not_established'
        : currentCount > 0 ? 'proven_true' : 'proven_false';
    const edition = pendingId ? document.activeRevisionId === null ? 'first' : activeId ? 'replacement' : 'unknown'
        : activeId ? 'current' : document.activeRevisionId === null ? 'first' : 'unknown';
    return {
        version: KNOWLEDGE_LIFECYCLE_VERSION,
        stored: id(document.id) ? 'proven_true' : 'not_established',
        source: { edition, revisionId: selectedId,
            fileName: sourceEstablished ? name(selected.fileName) : null,
            sizeBytes: sourceEstablished ? bytes(selected.fileSize) : null },
        indexing: { phase, verdict: phase === 'completed' ? 'proven_true'
                : phase === 'failed' || phase === 'removed' ? 'proven_false' : 'not_established' },
        search: { verdict: searchable, chunkCount: excluded ? 0 : currentCount },
        failure, qualifications,
    };
}
export function projectKnowledgeRepositoryMetrics(value) {
    if (!Array.isArray(value))
        return { version: KNOWLEDGE_LIFECYCLE_VERSION,
            uploaded: { documentCount: null, sizeBytes: null }, searchable: { documentCount: null, chunkCount: null }, removedDocumentCount: null };
    const documents = value.map(record);
    const current = documents.filter(document => !document.deletedAt);
    const projections = current.map(projectKnowledgeDocumentLifecycle);
    let sizeBytes = 0;
    let searchableCount = 0;
    let chunkCount = 0;
    for (const projection of projections) {
        const size = projection.source.sizeBytes;
        sizeBytes = sizeBytes === null || size === null || !Number.isSafeInteger(sizeBytes + size) ? null : sizeBytes + size;
        const search = projection.search;
        if (search.verdict === 'not_established') {
            searchableCount = null;
            chunkCount = null;
        }
        else if (search.verdict === 'proven_true') {
            if (searchableCount !== null)
                searchableCount++;
            chunkCount = chunkCount === null || search.chunkCount === null || !Number.isSafeInteger(chunkCount + search.chunkCount)
                ? null : chunkCount + search.chunkCount;
        }
    }
    return { version: KNOWLEDGE_LIFECYCLE_VERSION,
        uploaded: { documentCount: projections.every(projection => projection.stored === 'proven_true') ? current.length : null, sizeBytes },
        searchable: { documentCount: searchableCount, chunkCount },
        removedDocumentCount: documents.every(document => id(document.id) !== null) ? documents.length - current.length : null };
}
export function readKnowledgeDocumentLifecycle(value) {
    const result = knowledgeDocumentLifecycleSchema.safeParse(value);
    return result.success ? result.data : null;
}
export function describeKnowledgeFailure(failure) {
    switch (failure.code) {
        case 'MISSING_CREDENTIAL':
            return { message: 'Add an embedding credential in Organization Settings → Secrets before retrying.', action: 'review_credentials' };
        case 'INVALID_CREDENTIAL':
            return { message: 'The embedding credential could not be read. Review it in Organization Settings → Secrets before retrying.', action: 'review_credentials' };
        case 'INCOMPATIBLE_INDEX':
            return { message: 'Embedding settings differ from the saved index. Review the existing settings and reindex decision.', action: 'review_index' };
        case 'REQUEST_FAILED':
            if (failure.httpStatus === 401 || failure.httpStatus === 403)
                return {
                    message: `The embedding provider refused authentication or access (HTTP ${failure.httpStatus}). Review the organization embedding credential before retrying.`, action: 'review_credentials',
                };
            if (failure.httpStatus === null)
                return { message: 'The embedding provider outcome could not be confirmed. Check the saved processing status before retrying.', action: 'review_status' };
            return { message: `The embedding provider returned HTTP ${failure.httpStatus}. Review provider availability before a manual retry.`, action: 'retry' };
        case 'INVALID_RESPONSE':
            return { message: 'The embedding response could not be used. No complete index was published; review processing before retrying.', action: 'review_status' };
        case 'INVALID_SCOPE':
            return { message: 'The reference scope or input could not be used. Review the saved source and access.', action: 'review_source' };
        default:
            return { message: 'Processing did not complete. The uploaded reference is saved; review its current status before retrying.', action: 'retry' };
    }
}
//# sourceMappingURL=knowledgeLifecycle.js.map